import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { ButtonLink } from '@carouselgg/nexus/button'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { TrustGauge } from '@carouselgg/nexus/trust-gauge'
import { ArrowRight, ChevronRight, Eye, Pencil } from '@carouselgg/nexus/icon'
import { useAuthStore } from '@carouselgg/auth/store'
import { useMyOrgs } from '@carouselgg/auth/use-my-orgs'
import { useMyTeams } from '@carouselgg/auth/use-my-teams'
import { getMyInvites, type MyInvite } from '../../api/invites'
import { listMyTeamInvites, type MyTeamInvite } from '../../api/recruit'
import { getMyActivity, type MyEntry } from '../../api/myActivity'
import { rulesOwed as entriesOwingRules } from './competitionsModel'
import { HUB_PATHS } from '../../components/hub/hubSections'
import { accountTodos } from './accountTodos'
import styles from './AccountOverviewPage.module.css'

/** Shown before "N more" on the Overview; the full lists live in each section. */
const MAX_GAMES = 3
const MAX_TEAMS = 3

/**
 * The account hub's front page: who you are on Carousel, what your account is
 * waiting on, and a short look at each section with a link into it.
 */
export function AccountOverviewPage() {
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const trustScore = useAuthStore((s) => s.trustScore)
  const gameTrustScores = useAuthStore((s) => s.gameTrustScores)
  const phoneVerified = useAuthStore((s) => s.phoneVerified)
  const hasPasskey = useAuthStore((s) => s.hasPasskey)
  const teams = useMyTeams(isAuthenticated)
  const orgs = useMyOrgs(isAuthenticated)
  const [invites, setInvites] = useState<MyInvite[]>([])
  const [teamInvites, setTeamInvites] = useState<MyTeamInvite[]>([])
  const [owed, setOwed] = useState<MyEntry[]>([])

  useEffect(() => {
    if (!isAuthenticated) return
    let live = true
    getMyInvites()
      .then((r) => { if (live) setInvites(r.invites ?? []) })
      .catch(() => { if (live) setInvites([]) })
    // Each is best-effort: a failure leaves its rows out, not the page.
    listMyTeamInvites()
      .then((r) => { if (live) setTeamInvites(r.applications ?? []) })
      .catch(() => { /* none shown */ })
    getMyActivity()
      .then((a) => { if (live) setOwed(entriesOwingRules(a.entries)) })
      .catch(() => { /* none shown */ })
    return () => { live = false }
  }, [isAuthenticated])

  const todos = useMemo(() => user ? accountTodos({
    email: user.email,
    emailVerified: user.email_verified,
    invites,
    teamInvites,
    rulesOwed: owed,
    tips: trustScore?.tips ?? [],
  }) : [], [user, invites, teamInvites, owed, trustScore])

  if (!user) return null

  const name = user.gamer_tag || user.username
  const score = trustScore?.score ?? null
  const since = new Date(user.created_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
  const running = orgs.filter((o) => ['owner', 'admin', 'manager'].includes(o.role)).length
  const facts = [
    `${gameTrustScores.length} ${gameTrustScores.length === 1 ? 'game' : 'games'}`,
    `${teams.length} ${teams.length === 1 ? 'team' : 'teams'}`,
    running > 0 ? `runs ${running} ${running === 1 ? 'org' : 'orgs'}` : null,
    `since ${since}`,
  ].filter(Boolean)
  const games = [...gameTrustScores].sort((a, b) => b.score.score - a.score.score)

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <TrustGauge score={score ?? 0} size={112} title={score === null ? 'Trust score' : undefined}>
          {user.avatar_url
            ? <img src={user.avatar_url} alt="" className={styles.avatar} />
            : <span className={styles.initial}>{name.charAt(0).toUpperCase()}</span>}
        </TrustGauge>
        <div className={styles.heroText}>
          <span className={styles.eyebrow}>Your account</span>
          <DisplayHeading as="h1" size="6xl" className={styles.name}>{name}</DisplayHeading>
          <p className={styles.facts}>
            {score !== null && <strong className={styles.strong}>{trustScore?.level} · {score}</strong>}
            {facts.map((f) => <span key={f}> · {f}</span>)}
          </p>
        </div>
        <div className={styles.heroActions}>
          <ButtonLink to={`/u/${user.username}`} variant="secondary"><Eye size={16} aria-hidden="true" /> View public profile</ButtonLink>
          <ButtonLink to={HUB_PATHS.profile}><Pencil size={16} aria-hidden="true" /> Edit profile</ButtonLink>
        </div>
      </section>

      <section className={styles.needs} aria-labelledby="needs-title">
        <div className={styles.needsHead}>
          <DisplayHeading as="h2" size="3xl" id="needs-title" className={styles.flat}>Needs you</DisplayHeading>
          {todos.length > 0 && <span className={styles.muted}>{todos.length} {todos.length === 1 ? 'thing' : 'things'}</span>}
        </div>
        {todos.length === 0
          ? <p className={styles.muted}>Nothing. Your account is all set.</p>
          : (
            <ul className={styles.todoList}>
              {todos.map((t) => (
                <li key={t.id} className={styles.todo}>
                  <span className={styles.todoText}>
                    <span className={styles.todoTitle}>{t.title}</span>
                    <span className={styles.muted}>{t.detail}</span>
                  </span>
                  {t.points ? <span className={styles.points}>+{t.points}</span> : <span />}
                  {t.actionTo ? (
                    <ButtonLink to={t.actionTo} variant="secondary" size="sm">
                      {t.actionLabel} <ArrowRight size={14} aria-hidden="true" />
                    </ButtonLink>
                  ) : <span />}
                </li>
              ))}
            </ul>
          )}
      </section>

      <div className={styles.columns}>
        <section className={styles.section} aria-labelledby="games-title">
          <div className={styles.sectionHead}>
            <DisplayHeading as="h2" size="3xl" id="games-title" className={styles.flat}>Your games</DisplayHeading>
            <Link to={HUB_PATHS.games} className={styles.more}>Manage games</Link>
          </div>
          {games.length === 0
            ? <p className={styles.muted}>No games yet. <Link to={HUB_PATHS.games}>Link a game account</Link> to compete.</p>
            : (
              <ul className={styles.rows}>
                {games.slice(0, MAX_GAMES).map((g) => (
                  <li key={g.game_id}>
                    <Link to={HUB_PATHS.games} className={styles.row}>
                      <span className={styles.logoTile}>
                        {g.game_logo_url ? <img src={g.game_logo_url} alt="" /> : g.game_name.charAt(0)}
                      </span>
                      <span className={styles.rowText}>
                        <span className={styles.rowTitle}>{g.game_name}</span>
                        <span className={styles.muted}>{g.score.level}</span>
                      </span>
                      <TrustGauge score={g.score.score} size={32} />
                    </Link>
                  </li>
                ))}
                {games.length > MAX_GAMES && (
                  <li><Link to={HUB_PATHS.games} className={styles.moreRow}>{games.length - MAX_GAMES} more <ChevronRight size={14} aria-hidden="true" /></Link></li>
                )}
              </ul>
            )}
        </section>

        <section className={styles.section} aria-labelledby="signin-title">
          <div className={styles.sectionHead}>
            <DisplayHeading as="h2" size="3xl" id="signin-title" className={styles.flat}>Sign-in</DisplayHeading>
            <Link to={HUB_PATHS.signIn} className={styles.more}>Manage</Link>
          </div>
          <ul className={styles.rows}>
            <ProofRow label="Email" detail={user.email} done={user.email_verified} doneText="Verified" todoText="Not verified" to={`${HUB_PATHS.signIn}#email`} />
            <ProofRow label="Phone" detail="One phone, one Carousel account" done={phoneVerified} doneText="Verified" todoText="Not verified" to={`${HUB_PATHS.signIn}#phone`} />
            <ProofRow label="Passkey" detail="Sign in with your face or fingerprint" done={hasPasskey} doneText="Saved" todoText="None yet" to={`${HUB_PATHS.signIn}#security`} />
          </ul>
        </section>

        <section className={styles.section} aria-labelledby="teams-title">
          <div className={styles.sectionHead}>
            <DisplayHeading as="h2" size="3xl" id="teams-title" className={styles.flat}>Teams &amp; clubs</DisplayHeading>
            <Link to={HUB_PATHS.teams} className={styles.more}>Manage</Link>
          </div>
          {teams.length === 0 && orgs.length === 0
            ? <p className={styles.muted}>You&apos;re not on a team yet. <Link to="/compete">Find a competition</Link> or <Link to="/orgs">browse clubs</Link>.</p>
            : (
              <ul className={styles.rows}>
                {teams.slice(0, MAX_TEAMS).map((t) => (
                  <li key={t.id}>
                    <Link to={HUB_PATHS.teams} className={styles.row}>
                      <span className={styles.logoTile}>
                        {t.logoUrl || t.orgLogoUrl ? <img src={t.logoUrl || t.orgLogoUrl} alt="" /> : t.name.charAt(0)}
                      </span>
                      <span className={styles.rowText}>
                        <span className={styles.rowTitle}>{t.name}</span>
                        <span className={styles.muted}>{t.orgName} · {t.gameName}</span>
                      </span>
                      <span className={styles.role}>{roleLabel(t.role)}</span>
                    </Link>
                  </li>
                ))}
                {orgs.length > 0 && (
                  <li>
                    <Link to={HUB_PATHS.teams} className={styles.moreRow}>
                      {orgs.length} {orgs.length === 1 ? 'organization' : 'organizations'}
                      {running > 0 && <> · you run {running}</>}
                      <ChevronRight size={14} aria-hidden="true" />
                    </Link>
                  </li>
                )}
              </ul>
            )}
        </section>
      </div>
    </div>
  )
}

function ProofRow({ label, detail, done, doneText, todoText, to }: {
  label: string
  detail: string
  done: boolean
  doneText: string
  todoText: string
  to: string
}) {
  return (
    <li>
      <Link to={to} className={styles.row}>
        <span className={styles.rowText}>
          <span className={styles.rowTitle}>{label}</span>
          <span className={styles.muted}>{detail}</span>
        </span>
        <span className={done ? styles.role : styles.warn}>{done ? doneText : todoText}</span>
      </Link>
    </li>
  )
}

function roleLabel(role: string): string {
  return role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Player'
}
